import * as yup from 'yup';
import {DatePickerType, TaskField, TaskFieldRequiredState} from '../../../../../../../constants/tasks';
import {FormattedFormField} from '../../../../../types';
import {validationErrors} from '../../../../../../../constants/constants';
import {meetsVisibilityCriteria} from './meetsVisibilityCriteria';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {calculateMinDate} from '../../../../../../common/date_time_picker/helpers';

dayjs.extend(isSameOrBefore);

const getSingleSelectValidationSchema = (isRequired) => {
  return isRequired ?
    yup.string().required(validationErrors.required).nullable() :
    yup.string().nullable();
};

const getMultiSelectValidationSchema = (isRequired) => {
  return isRequired ?
    yup.array().of(yup.string())
        .required(validationErrors.required).min(1, validationErrors.required) :
    yup.array().nullable();
};

const getTextValidationSchema = (isRequired, details) => {
  const {maxlength, regexp} = details || {};

  let schema = yup.string();

  if (maxlength) {
    schema = schema.max(maxlength, validationErrors.maxLength(maxlength));
  }

  if (regexp) {
    schema = schema.matches(new RegExp(regexp), validationErrors.validValue);
  }

  if (isRequired) {
    schema = schema.required(validationErrors.required);
  }

  return schema.nullable();
};

const getDateValidationSchema = ({
  isRequired, validationType, type, details
}) => {
  const minDate = calculateMinDate(type, validationType);
  const limitUnit = type === DatePickerType.DATE ? 'day' : 'minute';
  const {default: now} = details;

  let schema = yup.object().test('is-min', validationErrors.dateTime, (date: any) => {
    const dateToValidate = !date && now ? dayjs() : date;

    if (minDate) {
      return minDate.isSameOrBefore(dateToValidate, limitUnit);
    }

    return dayjs().isSameOrBefore(dateToValidate, limitUnit);
  }

  );

  if (isRequired) {
    schema = schema.required(validationErrors.required);
  }

  return schema.nullable();
};

const getValidationSchemaByType = (
    type, isRequired, details, validationType
) => {
  switch (type) {
    case TaskField.TEXT:
      return getTextValidationSchema(isRequired, details);
    case TaskField.SELECT:
    case TaskField.TREATMENT_SELECT:
    case TaskField.OPERATORY_SELECT:
      return getSingleSelectValidationSchema(isRequired);
    case TaskField.MULTI_SELECT:
      return getMultiSelectValidationSchema(isRequired);
    case TaskField.DATE:
      return getDateValidationSchema({
        isRequired, validationType, type: DatePickerType.DATE, details
      });
    case TaskField.DATE_TIME:
      return getDateValidationSchema({
        isRequired, validationType, type: DatePickerType.DATE_TIME, details
      });
    default:
      return yup.object().nullable();
  }
};

export const generateValidationSchema = (
    questions: FormattedFormField[] = []
) => yup.lazy((values) => {
  return yup.object().shape({
    ...questions
        .reduce((
            acc, {id, type, required, details, conditions, validationType}
        ) => {
          const isRequired = required === TaskFieldRequiredState.REQUIRED;

          if (required === TaskFieldRequiredState.REQUIRED_BY_CONDITION) {
            const isRequiredByCondition =
              meetsVisibilityCriteria(required, conditions, values);

            acc[id] =
              getValidationSchemaByType(
                  type, isRequiredByCondition, details, validationType
              );

            return acc;
          }

          acc[id] =
          getValidationSchemaByType(type, isRequired, details, validationType);

          return acc;
        }, {})
  });
});

